import dayjs from "dayjs";
import React, { FC, useEffect, useMemo } from "react";
import { IconColorE, IconSizeE, UserRoleE } from "../../models/enums";
import { DownloadLogT } from "../../models/models";
import {
  DATE_FORMAT,
  MAX_TRANSFERRING_VIA_FTP_TIME_IN_MS,
} from "../../utils/constants";
import { clss } from "../../utils/helpers/styleHelpers";
import { useTranslate } from "../../utils/hooks/useTranslate";
import Icon from "../Icon/Icon";
import { isMobileOrTablet } from "../../utils/helpers/mediaQueries";

import styles from "./logRow.module.scss";
import { useAppSelector } from "../../store/hooks";
import { selectCurrentUser } from "../../store/selectors/authSelectors";

type LogRowPropTypes = {
  item: DownloadLogT;
};

enum ModeNameE {
  FTPUpload = "FTPUpload",
}

const LogRow: FC<LogRowPropTypes> = (props) => {
  const {
    item: {
      Progress,
      DownloadDateTime,
      UserFullName,
      UserId,
      ClientName,
      FileName,
      OriginalFileName,
      Error,
      ModeName,
    },
  } = props;
  const { t } = useTranslate();
  const isTimeout = useMemo(
    () =>
      dayjs().diff(dayjs(DownloadDateTime)) >
      MAX_TRANSFERRING_VIA_FTP_TIME_IN_MS,
    [DownloadDateTime]
  );
  const user = useAppSelector(selectCurrentUser);

  // show upload button for broadcaster
  useEffect(() => {
    if ((user?.role === UserRoleE.Broadcaster || user?.role === UserRoleE.AdvertisingAgency) && user?.userId === UserId) {
      const AdId = OriginalFileName.split(".")?.[0];
      const showUploadButtonEvent = new CustomEvent(`${AdId}-upload-button`, {
        detail: (user?.role === UserRoleE.Broadcaster || user?.role === UserRoleE.AdvertisingAgency) && user?.userId === UserId,
      });

      document.dispatchEvent(showUploadButtonEvent);
    }
  }, [user?.userId, user?.role, UserId, OriginalFileName]);

  const LogIcon = () => {
    if (Error || (Progress !== null && isTimeout))
      return (
        <Icon
          name="exclamation"
          title={
            Error ||
            (ModeName === ModeNameE.FTPUpload
              ? t`Failed to transfer file via FTP`
              : t`Failed to download`)
          }
          className={styles.errorIcon}
          withoutTranslation
        />
      );
    if (Progress !== null)
      return (
        <Icon
          name="loading"
          className={styles.icon}
          size={IconSizeE.S}
          color={IconColorE.LIGHT_GRAY}
        />
      );
    return (
      <Icon
        name={ModeName === ModeNameE.FTPUpload ? "send" : "downloadFilled"}
        className={clss([
          styles.icon,
          ModeName === ModeNameE.FTPUpload
            ? styles.sentViaFTPICon
            : styles.downloadIcon,
        ])}
        title={
          ModeName === ModeNameE.FTPUpload
            ? "File sent via FTP"
            : "File downloaded"
        }
      />
    );
  };

  return (
    <div className={styles.wrapper}>
      <LogIcon />
      {!isMobileOrTablet() && (
        <span className={styles.percents}>
          {Progress !== null && !Error ? `${Progress}%` : ""}
        </span>
      )}
      <div className={styles.dataWrapper}>
        <span>{dayjs(DownloadDateTime).format(`${DATE_FORMAT} HH:mm:ss`)}</span>
        <span>{UserFullName}</span>
        <span>{ClientName}</span>
        <div className={styles.fileName}>
          <span>{FileName}</span>
          {FileName !== OriginalFileName && <span>{OriginalFileName}</span>}
        </div>
      </div>
    </div>
  );
};

export default LogRow;
